import React from 'react';

import type {
	GetStaticProps,
	GetStaticPropsContext,
	GetStaticPropsResult,
	NextPage,
} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';

import {
	Heading,
	Button,
	Text,
	Flex,
	useDisclosure,
	IconButton,
	useToast,
} from '@tempo/core';
import {useRouter} from 'next/router';
import NavBar from '@components/NavBar';

import type {Trackable} from '@customTypes/track';

import {useTranslation} from 'next-i18next';
import BottomSheet from '@components/BottomSheet';
import {CrossIcon, MoreHorizontalIcon} from '@tempo/icons';
import {HEADER_SIZE} from '@components/BottomSheet/Header';
import config from '@modules/config';
import {useUser} from '@providers/UserProvider';
import {useLegacyUser} from '@deezer/react-user';
import useTmmRedirect from '@hooks/useTmmRedirect';
import {
	MeDocument,
	useValidateOnboardingChooseNextStepStepMutation,
} from '@graphql/generated';
import {INTENT} from '@components/Toast';
import Toast from '@components/Toast';
import Container from '@components/Container';
import ArtistPickerDrawer from '@components/ArtistPickerDrawer';
import {ONBOARDING_STEPS, TMM_CAMPAIGN} from '@constants';

const OnboardingPage: NextPage<{locale: string}> = ({locale}) => {
	const {user, shouldBeOnboarded, onboardingSteps} = useUser();
	const router = useRouter();
	const {t} = useTranslation();
	const [userLegacy] = useLegacyUser();
	const fetchTmm = useTmmRedirect();

	const accountWebsiteSsl = config.get('accountWebsiteSsl');

	const toast = useToast({
		duration: 3000,
	});

	const {
		isOpen: isBottomSheetOpen,
		onOpen: onOpenBottomSheet,
		onClose: onCloseBottomSheet,
	} = useDisclosure();

	const {
		isOpen: isArtistPickerOpen,
		onOpen: onOpenArtistPicker,
		onClose: onCloseArtistPicker,
	} = useDisclosure();

	const [validateOnBoardingShaker, {loading}] =
		useValidateOnboardingChooseNextStepStepMutation({
			update: (cache, nextData) => {
				if (nextData?.data?.validateOnboardingChooseNextStepStep) {
					cache.updateQuery(
						{
							query: MeDocument,
						},
						(data) => ({
							...data,
							me: {
								...data.me,
								onboarding: {
									...data.me.onboarding,
									shouldBeOnboarded:
										nextData.data?.validateOnboardingChooseNextStepStep
											.onboarding.shouldBeOnboarded,
								},
							},
						}),
					);
				}
			},
			onError: () => {
				toast.closeAll();
				return toast({
					render: () => (
						<Toast intent={INTENT.ERROR}>
							{t('errormessage_text_anerroroccurredpleasetryagain_web')}
						</Toast>
					),
				});
			},
		});

	const logout = () => {
		router.push(`${accountWebsiteSsl}/${locale}/shaker/logout?path=/`);
	};

	const redirectToTmm = () => {
		const pathToRedirect = router.query.groupId
			? `/group/${router.query.groupId}/join?autojoin=true&source=${ONBOARDING_STEPS.IMPORT_LIBRARY}`
			: '/groups';

		fetchTmm(userLegacy, pathToRedirect, TMM_CAMPAIGN.ONBOARDING).then(
			(url) => {
				if (url) {
					router.push(url);
				}
			},
		);
	};

	const onValidateOnboarding = () => {
		validateOnBoardingShaker().then(() => router.push('/groups'));
	};

	React.useEffect(() => {
		if (!shouldBeOnboarded) {
			if (router.query.groupId) {
				router.push(
					`/group/${router.query.groupId}/join?autojoin=true&source=${ONBOARDING_STEPS.ARTIST_PICKERS}`,
				);
			} else {
				router.push('/groups');
			}
		}
	}, [router, shouldBeOnboarded]);

	if (!shouldBeOnboarded) return null;

	return (
		<>
			<NavBar justify="flex-end">
				<IconButton
					icon={<MoreHorizontalIcon color="icon.neutral.primary.default" />}
					aria-label="user menu"
					variant="ghost"
					onClick={onOpenBottomSheet}
				/>
			</NavBar>
			<Container>
				<Flex flexDir="column" align="center">
					<Heading
						variant="heading.xl"
						color="text.neutral.primary.default"
						mb="spacing.l"
					>
						{t('musictogether_title_importyourmusiclibrary_web')}
					</Heading>
					<Text
						variant="body.l.default"
						color="text.neutral.secondary.default"
						textAlign="center"
						mb="spacing.2xl"
					>
						{t('musictogether_text_usedzrlibraryorotherplatform_web', {
							sprintf: ['Deezer', 'Spotify', 'Apple Music'],
						})}
					</Text>
					{onboardingSteps.includes(ONBOARDING_STEPS.DEEZER_LIBRARY) && (
						<Button
							mb="spacing.s"
							isLoading={loading}
							onClick={onValidateOnboarding}
						>
							{t('musictogether_action_usedzrlibrary_web')}
						</Button>
					)}
					{onboardingSteps.includes(ONBOARDING_STEPS.IMPORT_LIBRARY) && (
						<Button
							variant={
								onboardingSteps.includes(ONBOARDING_STEPS.DEEZER_LIBRARY)
									? 'outline'
									: 'contain'
							}
							mb="spacing.s"
							onClick={redirectToTmm}
						>
							{onboardingSteps.includes(ONBOARDING_STEPS.DEEZER_LIBRARY)
								? t('musictogether_action_useanotherplatform_web')
								: t('musictogether_action_importmymusiclibrary_web')}
						</Button>
					)}
					{onboardingSteps.includes(ONBOARDING_STEPS.ARTIST_PICKERS) && (
						<Button
							variant={
								onboardingSteps.includes(ONBOARDING_STEPS.IMPORT_LIBRARY)
									? 'ghost'
									: 'outline'
							}
							isLoading={loading}
							onClick={onOpenArtistPicker}
						>
							{t('libraryimporteronboarding_action_chooseartistsmanually_web')}
						</Button>
					)}
				</Flex>
				<BottomSheet onClose={onCloseBottomSheet} isOpen={isBottomSheetOpen}>
					<BottomSheet.Header size={HEADER_SIZE.S}>
						<BottomSheet.Header.Title>{user?.name}</BottomSheet.Header.Title>
					</BottomSheet.Header>

					<BottomSheet.Button onClick={logout} icon={<CrossIcon />}>
						{t('generic_action_logout_web')}
					</BottomSheet.Button>
				</BottomSheet>
				{isArtistPickerOpen && (
					<ArtistPickerDrawer
						isOpen={isArtistPickerOpen}
						onClose={onCloseArtistPicker}
					/>
				)}
			</Container>
		</>
	);
};

export const getStaticProps: GetStaticProps = async ({
	locale,
	locales,
}): Promise<
	GetStaticPropsResult<GetStaticPropsContext & {tracking: Trackable}>
> => {
	return {
		props: {
			locale,
			locales,
			tracking: {pagename: 'music-together'},
			...(await serverSideTranslations(locale || '', ['common'])),
		},
	};
};

export default OnboardingPage;
